import type { ApplicationContext } from '@hc-frontend/core-context';
import { createErrorOperationResult } from '@hc-frontend/core-utils-business-operation';

import { UpdateProspectProductTypeParameters } from '../../entities';
import type {
  UpdateProspectProductTypeRepository,
  UpdateProspectProductTypeResult,
} from '../../repositories';

/**
 * Sets the product type on the current Lead Storage prospect.
 */
export type UpdateProspectProductTypeUseCase = (
  parameters: UpdateProspectProductTypeParameters,
) => Promise<UpdateProspectProductTypeResult>;

type UpdateProspectProductTypeFactoryParams = Pick<
  ApplicationContext,
  'log'
> & {
  provider: UpdateProspectProductTypeRepository;
};

/**
 * Creates the {@link UpdateProspectProductTypeUseCase}.
 *
 * @param params - The repository used to persist the product type and the logger.
 * @returns The use case. Validation and repository failures are logged and returned as error results.
 */
export function updateProspectProductTypeFactory({
  provider,
  log,
}: UpdateProspectProductTypeFactoryParams): UpdateProspectProductTypeUseCase {
  return async (parameters) => {
    const convertResult =
      UpdateProspectProductTypeParameters.safeParse(parameters);

    if (!convertResult.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToUpdateProspectProductType',
        data: {
          rootCause: convertResult.error,
        },
      });
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToUpdateProspectProductType',
            rootCause: convertResult.error,
          },
        ],
      });
    }

    const result = await provider.update(convertResult.data);

    if (!result.success) {
      log({
        context: 'server',
        type: 'error',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });
    }

    return result;
  };
}
